// src/components/BookingForm.js
import React, { useState } from 'react';
import { BRANDING, formatTimeSlot } from '../utils/helpers';
import { CheckCircle, MessageCircle } from 'lucide-react';

const BookingForm = ({ config, appointments, onBook }) => {
  const [form, setForm] = useState({ 
    nombre: '', cedula: '', edad: '', email: '', sexo: 'M', 
    telefono: '', direccion: '', motivo: '', tipo: 'Consulta', 
    date: '', paymentMethod: 'Pago Móvil', 
    checkHipertension: false, checkDiabetes: false, 
    otherBackground: '', medications: '', 
    emergencyContactName: '', emergencyContactPhone: '' 
  });

  // Estado para controlar el Modal de Éxito
  const [showSuccess, setShowSuccess] = useState(false);
  const [finalData, setFinalData] = useState(null);

  const maxDate = new Date(); 
  maxDate.setDate(new Date().getDate() + 14);

  // --- LÓGICA INTELIGENTE: ENCONTRAR PRIMER HUECO LIBRE ---
  const findFirstAvailableSlot = (dateString, existingApps) => {
      // 1. Buscamos todas las citas activas de ese día
      const activeApps = existingApps.filter(a => a.date === dateString && a.status !== 'Cancelada');
      const takenTimes = activeApps.map(a => a.assignedTime);

      // 2. Probamos horarios desde el slot 0 en adelante hasta encontrar uno libre
      // (Asumiendo que formatTimeSlot(0) es la hora de apertura, ej: 8:00 AM)
      let slotIndex = 0;
      let foundTime = null;
      
      // Buscamos hasta 50 turnos por seguridad
      while (!foundTime && slotIndex < 50) {
          const theoreticallyTime = formatTimeSlot(slotIndex);
          if (!takenTimes.includes(theoreticallyTime)) {
              foundTime = theoreticallyTime;
          } else {
              slotIndex++;
          }
      }
      return foundTime || formatTimeSlot(activeApps.length); // Fallback al final si falla algo
  };
  
  const handleSubmit = (e) => { 
      e.preventDefault(); 
      
      const [y,m,d] = form.date.split('-'); 
      const date = new Date(y, m-1, d, 12, 0, 0); 
      
      // 1. Validar Fines de Semana
      if(date.getDay() === 0 || date.getDay() === 6) return alert("No hay consultas fines de semana."); 
      
      // 2. Validar Límites
      const appsForDay = appointments.filter(a => a.date === form.date && a.status !== 'Cancelada');
      const count = appsForDay.length; 
      
      const limitRegular = parseInt(config.dailyLimit) || 10;
      const limitExam = parseInt(config.examLimit) || 2;
      const currentLimit = form.tipo === 'Examen' ? limitExam : limitRegular;

      if (count >= currentLimit) {
          return alert(`ESTIMADO PACIENTE:\n\nLo sentimos, los cupos para la fecha ${form.date} están AGOTADOS.\n\nPor favor seleccione otro día.`); 
      }
  
      // 3. ASIGNAR HORA (Usando lógica de huecos)
      const assignedTime = findFirstAvailableSlot(form.date, appointments);

      // 4. PREPARAR DATOS
      const bookingData = { ...form, assignedTime };
      
      // 5. MOSTRAR MODAL Y GUARDAR
      setFinalData(bookingData);
      setShowSuccess(true);
      onBook(bookingData); // Guarda en Firebase en segundo plano
  };
  
  const handleType = (e) => { 
      const val = e.target.value; 
      setForm(prev => ({ 
          ...prev, 
          tipo: val, 
          paymentMethod: val === 'Examen' ? 'Exonerado (Revisión)' : 'Pago Móvil' 
      })); 
  };

  // --- FUNCIÓN PARA EL BOTÓN DE WHATSAPP EN EL MODAL ---
  const handleWhatsAppRedirect = () => {
    if(!finalData) return;
    const text = `Hola, soy ${finalData.nombre}. He solicitado una cita para el ${finalData.date} a las ${finalData.assignedTime}.`;
    const url = `https://wa.me/58${config.whatsapp}?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
    window.location.reload();
  };

  // --- RENDERIZADO DEL MODAL DE ÉXITO ---
  if (showSuccess && finalData) {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 md:p-8 max-w-md w-full text-center relative animate-bounce-in">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="text-green-600" size={40} />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">¡Solicitud Exitosa!</h2>
                
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-900 mb-6 text-left shadow-inner">
                    <p className="font-bold border-b border-blue-200 pb-1 mb-2">Resumen de la Cita:</p>
                    <ul className="space-y-1">
                        <li>👤 <strong>Paciente:</strong> {finalData.nombre}</li>
                        <li>📅 <strong>Fecha:</strong> {finalData.date}</li>
                        <li>⏰ <strong>Hora Asignada:</strong> {finalData.assignedTime}</li>
                    </ul>
                    <div className="mt-3 text-xs italic text-gray-600 bg-white p-2 rounded border">
                        {config.successMessage || "Su cita ha sido pre-agendada en el sistema. Para confirmar su asistencia, por favor envíe el mensaje a nuestro WhatsApp."}
                    </div>
                </div>

                <button onClick={handleWhatsAppRedirect} className="w-full bg-green-600 text-white font-bold py-3 rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 mb-3 shadow-lg transform hover:scale-105 transition duration-200">
                    <MessageCircle size={20}/> CONFIRMAR EN WHATSAPP
                </button>
                
                <button onClick={() => window.location.reload()} className="text-sm text-gray-400 underline hover:text-gray-600 mt-2">
                    Cerrar ventana
                </button>
            </div>
        </div>
    );
  }
  
  // --- FORMULARIO NORMAL ---
  return (
    <div className="max-w-3xl mx-auto bg-white p-8 rounded-lg shadow-lg">
      <h2 className={`text-2xl font-bold ${BRANDING.accentColor} mb-2`}>Solicitud de Cita</h2>
      
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 text-sm">
          <p className="font-bold text-yellow-800">Información Importante</p>
          <p>{config.importantInfo || "Recuerde llegar 15 minutos antes."}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* FILA 1: DATOS BÁSICOS */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
              <label className="text-xs font-bold">Cédula</label>
              <input required className="w-full border p-2 rounded" value={form.cedula} onInput={e => setForm({...form, cedula: e.target.value.replace(/[^0-9]/g, '')})}/>
          </div>
          <div>
              <label className="text-xs font-bold">Nombre</label>
              <input required className="w-full border p-2 rounded" value={form.nombre} onInput={e => setForm({...form, nombre: e.target.value.replace(/[^a-zA-Z\sñÑ]/g, '')})}/>
          </div>
          <div>
              <label className="text-xs font-bold">Teléfono</label>
              <input required className="w-full border p-2 rounded" value={form.telefono} onInput={e => setForm({...form, telefono: e.target.value.replace(/[^0-9]/g, '')})}/>
          </div>
          <div className="grid grid-cols-2 gap-2">
             <div>
                 <label className="text-xs font-bold">Edad</label>
                 <input required type="number" className="w-full border p-2 rounded" onChange={e => setForm({...form, edad: e.target.value})}/>
             </div>
             <div>
                 <label className="text-xs font-bold">Sexo</label>
                 <select className="w-full border p-2 rounded" onChange={e => setForm({...form, sexo: e.target.value})}>
                     <option value="M">M</option>
                     <option value="F">F</option>
                 </select>
             </div>
          </div>
        </div>

        <div>
            <label className="text-xs font-bold">Email (Opcional)</label>
            <input className="w-full border p-2 rounded" onChange={e => setForm({...form, email: e.target.value})}/>
        </div>

        {/* FILA 2: DATOS MÉDICOS */}
        <div className="bg-blue-50 p-3 rounded border border-blue-100">
            <p className="text-xs font-bold text-blue-800 mb-2">Datos Médicos</p>
            <div className="flex gap-4 text-sm mb-2">
                <label><input type="checkbox" onChange={e => setForm({...form, checkHipertension: e.target.checked})}/> Hipertensión arterial </label>
                <label><input type="checkbox" onChange={e => setForm({...form, checkDiabetes: e.target.checked})}/> Diabetes </label>
            </div>
            <input placeholder="Otros antecedentes..." className="w-full border rounded p-1 mb-2 text-sm" onChange={e => setForm({...form, otherBackground: e.target.value})}/>
            <input placeholder="Medicamentos (Cual y Dosis)..." className="w-full border rounded p-1 text-sm" onChange={e => setForm({...form, medications: e.target.value})}/>
        </div>

        {/* FILA 3: TIPO Y FECHA */}
        <div className="grid grid-cols-2 gap-4">
            <div>
                <label className="text-xs font-bold">Tipo</label>
                <select className="w-full border p-2 rounded" onChange={handleType}>
                    <option value="Consulta">Consulta</option>
                    <option value="Examen">Revisión Exámenes</option>
                </select>
            </div>
            <div>
                <label className="text-xs font-bold">Fecha</label>
                <input required type="date" className="w-full border p-2 rounded" 
                    min={new Date(Date.now()+86400000).toISOString().split('T')[0]} 
                    max={maxDate.toISOString().split('T')[0]} 
                    onChange={e => { 
                        const [y,m,d] = e.target.value.split('-'); 
                        const date = new Date(y, m-1, d, 12, 0, 0); 
                        if(date.getDay()===0 || date.getDay()===6){
                            alert("Fines de semana cerrados");
                            e.target.value="";
                        } else {
                            setForm({...form, date: e.target.value})
                        } 
                    }}
                />
            </div>
        </div>

        {/* FILA 4: CONTACTO EMERGENCIA */}
        <div className="grid grid-cols-2 gap-4">
            <div>
                <label className="text-xs font-bold">Nombre Familiar (Opcional)</label>
                <input className="w-full border p-2 rounded" onInput={e => setForm({...form, emergencyContactName: e.target.value.replace(/[^a-zA-Z\sñÑ]/g, '')})}/>
            </div>
            <div>
                <label className="text-xs font-bold">Teléfono Familiar (Opcional)</label>
                <input className="w-full border p-2 rounded" onInput={e => setForm({...form, emergencyContactPhone: e.target.value.replace(/[^0-9]/g, '')})}/>
            </div>
        </div>

        {/* FILA 5: PAGO Y MOTIVO */}
        <div>
            <label className="text-xs font-bold">Pago</label>
            {form.tipo === 'Examen' && <p className="text-xs text-red-600 bg-red-50 p-1 rounded">⚠️ Exonerado solo para revisión (Max 40 días).</p>}
            <select disabled={form.tipo === 'Examen'} value={form.paymentMethod} className={`w-full border p-2 rounded ${form.tipo === 'Examen' ? 'bg-gray-200' : 'bg-white'}`} onChange={e => setForm({...form, paymentMethod: e.target.value})}>
                {form.tipo !== 'Examen' && <><option>Pago Móvil</option><option>Transferencia</option><option>Divisas</option><option>Punto de Venta</option></>}
                {form.tipo === 'Examen' && <option>Exonerado (Revisión)</option>}
            </select>
        </div>
        
        <textarea required placeholder="Motivo..." className="w-full border p-2 rounded" rows="2" onChange={e => setForm({...form, motivo: e.target.value})}></textarea>
        
        <button type="submit" className={`w-full ${BRANDING.secondaryColor} text-white py-4 rounded font-bold hover:opacity-90 transition`}>ENVIAR SOLICITUD</button>
      </form>
    </div>
  );
};

export default BookingForm;